/**
 * Multiplex WebSocket Client Reference Counter & Frame Builders.
 *
 * Manages per-symbol handler registries, desired symbols set, and ref-counted subscribe/unsubscribe frames.
 */

export interface MultiplexSubscriptionState {
  handlers: Record<string, Set<Function>>;
  desired: Set<string>;
}

export interface SubscriptionFrame {
  cmd: "subscribe" | "unsubscribe";
  symbols: string[];
}

/**
 * Builds standard command frame for websocket multiplex subscriptions.
 */
export function buildSubscriptionFrame(
  cmd: "subscribe" | "unsubscribe",
  symbols: string[],
): SubscriptionFrame {
  return {
    cmd,
    symbols: Array.isArray(symbols) ? symbols : [symbols],
  };
}

/**
 * Registers a handler for a symbol and returns whether this was the first subscriber.
 */
export function addSymbolSubscription(
  state: MultiplexSubscriptionState,
  sym: string,
  handler: Function,
): { isFirst: boolean } {
  if (!sym || !handler) return { isFirst: false };

  if (!state.handlers[sym]) {
    state.handlers[sym] = new Set();
  }
  state.handlers[sym].add(handler);

  const isFirst = !state.desired.has(sym);
  state.desired.add(sym);

  return { isFirst };
}

/**
 * Unregisters a handler for a symbol and returns whether the last consumer has been removed.
 */
export function removeSymbolSubscription(
  state: MultiplexSubscriptionState,
  sym: string,
  handler?: Function,
): { isLast: boolean } {
  const hs = state.handlers[sym];
  if (!hs) return { isLast: false };

  if (handler) {
    hs.delete(handler);
  } else {
    hs.clear();
  }

  if (hs.size === 0) {
    delete state.handlers[sym];
    state.desired.delete(sym);
    return { isLast: true };
  }

  return { isLast: false };
}
